import { jsPDF } from "jspdf";

const MAGENTA = [158, 18, 82];
const TEXT = [25, 25, 25];

function filename(value) {
  return `${String(value || "ATLAS DUMMY DOCUMENT").replace(/[^a-zA-Z0-9 _-]/g, "").trim()}.pdf`;
}

function clientName(message) {
  return String(message.client || "SAMPLE, CLIENT").replace(" (SAMPLE)", "").split(",").reverse().join(" ").trim();
}

function grantNumber(message) {
  return `DUMMY-${String(message.fileExt || "26A000000/0").replace("/", "-")}`;
}

function createWriter(pdf, message) {
  let y = 18;
  let page = 1;
  const margin = 29;
  const width = 152;
  const footer = () => {
    pdf.setTextColor(90, 90, 90);
    pdf.setFont("helvetica", "normal");
    pdf.setFontSize(7);
    pdf.text(`ATLAS fictional mock - ${message.dateReceived.split(" ")[0]}`, margin, 286);
    pdf.text(`Page ${page}`, 174, 286);
  };
  const newPage = () => { footer(); pdf.addPage(); page += 1; y = 22; };
  const ensure = (height = 15) => { if (y + height > 278) newPage(); };
  const text = (value, options = {}) => {
    const { bold = false, size = 9.8, gap = 4, indent = 0 } = options;
    pdf.setTextColor(...TEXT);
    pdf.setFont("helvetica", bold ? "bold" : "normal");
    pdf.setFontSize(size);
    const lines = pdf.splitTextToSize(String(value), width - indent);
    ensure(lines.length * size * 0.42 + gap);
    pdf.text(lines, margin + indent, y);
    y += lines.length * size * 0.42 + gap;
  };
  const heading = (value) => text(value, { bold: true, size: 11.5, gap: 6 });
  const section = (value) => text(value, { bold: true, size: 10.2, gap: 3 });
  const bullet = (value) => { ensure(12); pdf.setFillColor(...TEXT); pdf.circle(margin + 5, y - 1, 0.8, "F"); text(value, { indent: 11, gap: 3 }); };
  const header = (recipient = "LAW FIRM") => {
    pdf.setFillColor(...MAGENTA);
    pdf.roundedRect(145, 15, 11, 11, 2, 2, "F");
    pdf.setTextColor(45, 55, 65);
    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(15);
    pdf.text("Victoria Legal Aid", 159, 24);
    pdf.setFontSize(7.5);
    pdf.text(`VLA grant number: ${grantNumber(message)}`, margin, 20);
    pdf.text("Lawyer reference: DEMO-LACW", margin, 25);
    pdf.text("Telephone: (03) 9000 0000", margin, 31);
    pdf.setFont("helvetica", "normal");
    pdf.text("Fictional simulator contact details", margin, 36);
    pdf.text("Email: simulator@example.test", margin, 41);
    pdf.setFont("helvetica", "bold");
    pdf.text("Melbourne Office", 154, 34);
    pdf.setFont("helvetica", "normal");
    pdf.text("Level 9, 570 Sample Street", 154, 39);
    pdf.text("Melbourne VIC 3000", 154, 44);
    pdf.setTextColor(...MAGENTA);
    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(8);
    pdf.text("FICTIONAL MOCK - NOT AN OFFICIAL VLA LETTER", margin, 53);
    y = 67;
    text(message.dateReceived.split(" ")[0], { gap: 8 });
    text(recipient, { gap: 2 });
    text("PO BOX 100", { gap: 2 });
    text("MELBOURNE VIC 3000", { gap: 14 });
  };
  const close = () => { text("Thank you for supporting access to justice.", { size: 9.5, gap: 5 }); text("Yours faithfully", { size: 9.5, gap: 5 }); text("Victoria Legal Aid - fictional simulator", { size: 9.5, gap: 2 }); footer(); };
  return { text, heading, section, bullet, header, close, footer };
}

function approvalLetter(pdf, message, clientCopy, extension) {
  const w = createWriter(pdf, message);
  const name = clientName(message);
  w.header(clientCopy ? `${name.toUpperCase()}\nC/- LAW FIRM` : "LAW FIRM");
  w.text(clientCopy ? `Dear ${name.split(" ")[0]}` : "Dear Practitioner", { gap: 10 });
  w.heading(extension ? (clientCopy ? "Outcome of your application for more legal assistance" : `Legal aid application - ${name}`) : (clientCopy ? "Outcome of your legal aid application" : `Legal aid application - ${name}`));
  w.text(extension ? `Further legal assistance has been approved for this fictional criminal law matter in the County Court. Assistance is effective from 1 August 2026.` : `Legal assistance has been approved for this fictional criminal law matter in the Magistrates' Court. Assistance is effective from 1 August 2026.`);
  w.section(extension ? "Approved further legal assistance" : "Approved legal assistance");
  w.text(extension ? "OTHER DISBURSEMENT\nA sample allowance of $2,750 is approved for a fictional psychological assessment and report." : "Magistrates' Court - summary hearing\nFee: Sample lump sum under the applicable fictional fee schedule.\nThis mock grant does not authorise any real work or payment.");
  w.section(clientCopy ? "No financial contribution at this stage" : "Fees and disbursements");
  w.text(clientCopy ? "No contribution is requested in this simulator example. This may change in a real matter if financial circumstances or the scope of assistance changes." : "The ATLAS pro forma invoice would ordinarily describe what can be claimed. This fictional document creates no entitlement to payment.");
  w.section("Your obligations");
  w.text("Keep your lawyer informed of changes to your address, finances or circumstances. Any real grant would remain subject to the applicable guidelines and standard terms.");
  w.section("Right of reconsideration");
  w.text("A person affected by a real decision may have reconsideration rights under the Legal Aid Act 1978. This sample decision has no legal effect and requires no response.");
  w.close();
}

function refusalLetter(pdf, message, clientCopy) {
  const w = createWriter(pdf, message);
  const name = clientName(message);
  w.header(clientCopy ? `${name.toUpperCase()}\nC/- LAW FIRM` : "LAW FIRM");
  w.text(clientCopy ? `Dear ${name.split(" ")[0]}` : "Dear Practitioner", { gap: 10 });
  w.heading(clientCopy ? "Outcome of your application for more legal assistance" : `Legal aid application - ${name}`);
  w.text("The fictional application for further legal assistance has been refused. This mock letter explains the sample reason and reconsideration process.");
  w.section(clientCopy ? "Why we made our decision" : "Why we refused further legal assistance");
  w.text("The request did not contain enough information to identify the additional work or funding sought. A new extension with supporting details would be required in this example.");
  w.section("Right of reconsideration of our decision");
  w.text("In a real matter, the client may be entitled to ask for reconsideration within the stated time limit. This is a fictional simulator outcome and no request should be submitted.");
  if (clientCopy) { w.section("Interpreter services"); w.text("Free interpreter services may be available for genuine VLA correspondence. The contact details on this mock are intentionally non-operational."); }
  w.section("More information");
  w.text("For simulator questions, contact the LACW portal development team. Do not contact Victoria Legal Aid about this fictional record.");
  w.close();
}

function requestLetter(pdf, message) {
  const w = createWriter(pdf, message);
  const name = clientName(message);
  w.header("LAW FIRM");
  w.text("Dear Practitioner", { gap: 10 });
  w.heading(`Additional information - ${name}`);
  w.text("We need more fictional information to demonstrate how an ATLAS request appears in the portal.");
  w.section("Information requested");
  w.bullet("A short explanation of the sample special circumstances relied upon.");
  w.bullet("Fictional supporting documents showing why representation is required.");
  w.bullet("Sample bank statements for the previous three months.");
  w.bullet("Sample business records and tax returns where self-employment applies.");
  w.text("Please provide the fictional information by 22 August 2026. No real material should be uploaded to this simulator.");
  w.close();
}

function transferLetter(pdf, message) {
  const w = createWriter(pdf, message);
  const name = clientName(message);
  w.header("LAW FIRM");
  w.text("Dear Practitioner", { gap: 10 });
  w.heading(`Legal assistance transfer - ${name}`);
  w.text("A fictional transfer of legal assistance has been approved from the Sample Regional Office to your sample office for a family law matter in the Children's Court.");
  w.section("Approved legal assistance");
  w.text("Children's Court (Family Division) proceedings\nFee: Sample lump sum - stages 1 and 2.\nThis mock covers fictional preparation, appearances and conference work only.");
  w.section("No contribution at this stage");
  w.text("No contribution is requested in this fictional example. A genuine matter would depend on the client's finances and the extent of assistance.");
  w.section("Fees and disbursements");
  w.text("No fees may be claimed from this document. It is provided only to test downloading, filing and portal automation.");
  w.section("Right of reconsideration");
  w.text("This simulator decision has no legal effect. Genuine reconsideration rights would be explained in official correspondence.");
  w.close();
}

function creditNote(pdf, message) {
  const w = createWriter(pdf, message);
  w.heading("Adjustment note");
  w.text("FICTIONAL MOCK - NOT A TAX DOCUMENT", { bold: true, gap: 10 });
  const pairs = [["Payee Office", "LAW FIRM"], ["Business Address", "100 SAMPLE STREET MELBOURNE VIC 3000"], ["ABN", "00 000 000 000"], ["Payment Method", "SAMPLE EFT"], ["Date of Issue", message.dateReceived.split(" ")[0]], ["Claim ID", "DUMMY-6396939"], ["File/Ext", message.fileExt], ["Client Name", clientName(message).toUpperCase()], ["Practitioner", "EMMA SAMPLE"]];
  pairs.forEach(([label, value]) => w.text(`${label.padEnd(23, " ")} ${value}`, { gap: 2 }));
  w.section("Work Performed");
  w.text("GST shown below is fictional and is not payable.");
  w.text("DEFEND/CONTEST DAILY FEE     -1.00     GST -$94.36     TOTAL -$1,038.00", { gap: 3 });
  w.text("GENERAL PREPARATION          -          GST -$62.09     TOTAL -$683.00", { gap: 3 });
  w.text("SUBPOENA INSPECTION           -1.00     GST -$39.55     TOTAL -$435.00", { gap: 8 });
  w.text("TOTAL ADJUSTMENT                                      -$2,156.00", { bold: true, gap: 15 });
  w.section("Certification");
  w.text("This transaction is a fictional simulator record. It is not an adjustment note, credit note or claim.");
  w.close();
}

function genericDocument(pdf, message, title) {
  const w = createWriter(pdf, message);
  w.header("LAW FIRM");
  w.heading(title);
  w.text(`This is a fictional ${title.toLowerCase()} generated for the ATLAS simulator.`);
  w.text(`Client: ${clientName(message)}\nFile/Extension: ${message.fileExt}\nDocument size shown in inbox: ${message.size}`);
  w.section("Important notice");
  w.text("This document is not an official Victoria Legal Aid record, creates no entitlement and must not be used for a real client or matter.");
  w.close();
}

export function createAtlasSimulatorPdf(message, attachment = false) {
  const title = attachment ? message.attachment : message.documentType;
  const pdf = new jsPDF({ unit: "mm", format: "a4" });
  if (attachment) genericDocument(pdf, message, title);
  else if (title === "INITIAL APPROVAL - CLIENT") approvalLetter(pdf, message, true, false);
  else if (title === "INITIAL APPROVAL - OFFICE") approvalLetter(pdf, message, false, false);
  else if (title === "EXTENSION APPROVAL - CLIENT") approvalLetter(pdf, message, true, true);
  else if (title === "EXTENSION APPROVAL - OFFICE") approvalLetter(pdf, message, false, true);
  else if (title === "EXTENSION REFUSAL - CLIENT") refusalLetter(pdf, message, true);
  else if (title === "EXTENSION REFUSAL - OFFICE") refusalLetter(pdf, message, false);
  else if (title === "REQ - GENERAL - SUBMITTING OFFICE") requestLetter(pdf, message);
  else if (title === "TRANSFER APPROVAL - FROM OFFICE") transferLetter(pdf, message);
  else if (title === "CREDIT NOTE") creditNote(pdf, message);
  else genericDocument(pdf, message, title);
  return { pdf, title };
}

export function downloadAtlasSimulatorPdf(message, attachment = false) {
  const { pdf, title } = createAtlasSimulatorPdf(message, attachment);
  pdf.save(filename(title));
}
